import { create } from "zustand";

export type ToastKind = "success" | "error";

export interface ToastItem {
  id: string;
  message: string;
  kind: ToastKind;
}

interface ToastState {
  toasts: ToastItem[];
  /** Enqueue a toast. Returns the generated id (useful for manual dismissal). */
  addToast: (message: string, kind?: ToastKind) => string;
  /** Remove a toast by id (auto-dismiss timer or the dismiss button). */
  removeToast: (id: string) => void;
}

// Monotonic id source — deterministic and collision-free without Date.now/random.
let counter = 0;

/**
 * Ephemeral, client-only store for transient success/error toasts. Mounted once
 * via {@link Toaster} at the app root; fire from anywhere with the {@link toast}
 * helper (e.g. a TanStack Query mutation `onSuccess`/`onError`).
 */
export const useToastStore = create<ToastState>((set, get) => ({
  toasts: [],
  addToast: (message, kind = "success") => {
    // Collapse a burst of identical toasts (e.g. rapid preference toggles) into
    // the one already showing rather than stacking duplicates.
    const duplicate = get().toasts.find((t) => t.message === message && t.kind === kind);
    if (duplicate) return duplicate.id;
    const id = `toast-${String(++counter)}`;
    set((state) => ({ toasts: [...state.toasts, { id, message, kind }] }));
    return id;
  },
  removeToast: (id) => {
    set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) }));
  },
}));

/**
 * Imperative helper for raising toasts outside React render (mutation handlers,
 * event callbacks). Messages are rendered as escaped text by {@link Toaster}.
 */
export const toast = {
  success: (message: string) => useToastStore.getState().addToast(message, "success"),
  error: (message: string) => useToastStore.getState().addToast(message, "error"),
};
